/**
 * The MapLibre instance and its layers.
 *
 * MapLibre owns a canvas and a mutable style; React owns a tree it may re-render
 * at any time. Keeping the map in a ref and touching it only from effects is
 * what stops those two models fighting — the alternative, driving layers from
 * render, recreates the map on every state change and loses the viewport.
 *
 * The layers are added once, empty, on `load`. That is deliberate: the paint and
 * filter helpers then never have to ask whether a layer exists yet, which in the
 * legacy page caused a class of "the toggle does nothing the first time" bugs.
 */

import type { FeatureCollection } from "geojson";
import maplibregl from "maplibre-gl";
import { useEffect, useRef, useState } from "react";

import { getBounds } from "./lib/bounds";
import { SEQ_NONE } from "./lib/choropleth";
import { LETTER_COLORS, LETTER_NONE } from "./lib/letters";
import { TEMP_COLORS, TEMP_NONE } from "./lib/temperatura";
import { VIAB_COLORS } from "./lib/tramos";

export const EMPTY: FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

/** Layer and source ids, in one place so the effects cannot typo them. */
export const LAYERS = {
  buildingsSource: "buildings",
  buildingsFill: "buildings-fill",
  buildingsOutline: "buildings-outline",
  buildingsHover: "buildings-hover",
  tramosSource: "tramos",
  tramosLine: "tramos-line",
  distritosSource: "distritos",
  distritosFill: "distritos-fill",
  distritosLine: "distritos-line",
  distritosHover: "distritos-hover",
  distritosSelectedCasing: "distritos-selected-casing",
  distritosSelected: "distritos-selected",
} as const;

/** How the buildings layer is coloured. Two readings of the same stock. */
export type BuildingPaint = "letra" | "temperatura";

/** Paint expression per reading, so the toggle is one setPaintProperty. */
const BUILDING_PAINT: Record<BuildingPaint, maplibregl.ExpressionSpecification> = {
  letra: [
    "match", ["get", "letra"],
    "A", LETTER_COLORS.A,
    "B", LETTER_COLORS.B,
    "C", LETTER_COLORS.C,
    "D", LETTER_COLORS.D,
    "E", LETTER_COLORS.E,
    "F", LETTER_COLORS.F,
    "G", LETTER_COLORS.G,
    LETTER_NONE,
  ],
  temperatura: [
    "match", ["get", "t_class"],
    "high", TEMP_COLORS.high,
    "mid", TEMP_COLORS.mid,
    "low", TEMP_COLORS.low,
    "ambient", TEMP_COLORS.ambient,
    TEMP_NONE,
  ],
};

/**
 * Centre of the Comunitat Valenciana, at a zoom that frames the territory. The
 * map opens here before a municipality is chosen.
 */
const HOME = { center: [-0.376, 39.47] as [number, number], zoom: 8 };

export interface UseMapLibre {
  /** Attach to the div MapLibre should take over. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** True once the style has loaded and the layers exist. */
  ready: boolean;
  mapRef: React.MutableRefObject<maplibregl.Map | null>;
}

export function useMapLibre(): UseMapLibre {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      // Declared inline rather than fetched from a style server: one less origin
      // in the CSP, and one less thing that can be down.
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors",
          },
        },
        layers: [{ id: "osm", type: "raster", source: "osm" }],
      },
      ...HOME,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    map.on("load", () => {
      map.addSource(LAYERS.buildingsSource, { type: "geojson", data: EMPTY });

      map.addLayer({
        id: LAYERS.buildingsFill,
        type: "fill",
        source: LAYERS.buildingsSource,
        paint: {
          "fill-color": BUILDING_PAINT.letra,
          "fill-opacity": 0.78,
        },
      });

      map.addLayer({
        id: LAYERS.buildingsOutline,
        type: "line",
        source: LAYERS.buildingsSource,
        paint: { "line-color": "rgba(0,0,0,0.25)", "line-width": 0.5 },
      });

      // Hover highlight. A separate layer rather than a feature-state
      // expression, because the source is replaced wholesale on every
      // municipality load — that discards feature state but leaves a filter
      // intact.
      map.addLayer({
        id: LAYERS.buildingsHover,
        type: "fill",
        source: LAYERS.buildingsSource,
        filter: ["==", "id", ""],
        paint: { "fill-color": "#ffffff", "fill-opacity": 0.35 },
      });

      // ── Road segments ──────────────────────────────────────────────────
      // Drawn over the buildings: the question this layer answers is which
      // street could carry a network, and a street hidden under the blocks it
      // would serve cannot be read at all.
      map.addSource(LAYERS.tramosSource, { type: "geojson", data: EMPTY });
      map.addLayer({
        id: LAYERS.tramosLine,
        type: "line",
        source: LAYERS.tramosSource,
        layout: { visibility: "none" },
        paint: {
          "line-color": [
            "match", ["get", "viabilidad"],
            "ambos", VIAB_COLORS.ambos,
            "4g", VIAB_COLORS["4g"],
            "5g", VIAB_COLORS["5g"],
            "aero", VIAB_COLORS.aero,
            VIAB_COLORS.sin_datos,
          ],
          // Weighted by how many buildings the segment serves, so the streets
          // that matter read as the thick ones.
          "line-width": [
            "interpolate", ["linear"], ["get", "n_edificios"],
            0, 2.2, 10, 3.5, 30, 5,
          ],
          "line-opacity": 0.9,
        },
      });

      // ── District choropleth (city mode) ────────────────────────────────
      // Added unconditionally but hidden, like everything else here: the paint
      // and filter helpers then never have to ask whether a layer exists yet.
      map.addSource(LAYERS.distritosSource, { type: "geojson", data: EMPTY });

      // Inserted BEFORE the tramos line so the street network keeps drawing on
      // top of the choropleth. Final order: buildings → choropleth → tramos →
      // district strokes.
      map.addLayer({
        id: LAYERS.distritosFill,
        type: "fill",
        source: LAYERS.distritosSource,
        layout: { visibility: "none" },
        // Translucent so the building letters stay readable underneath: the
        // choropleth is an overlay on the stock, not a replacement for it.
        paint: { "fill-color": SEQ_NONE, "fill-opacity": 0.62 },
      }, LAYERS.tramosLine);

      map.addLayer({
        id: LAYERS.distritosLine,
        type: "line",
        source: LAYERS.distritosSource,
        layout: { visibility: "none" },
        paint: {
          "line-color": "rgba(33,33,33,0.35)",
          "line-width": 0.8,
        },
      });

      // Hover and selection rings: a stroke each, so the outline reads against
      // whichever ramp step is underneath it.
      map.addLayer({
        id: LAYERS.distritosHover,
        type: "line",
        source: LAYERS.distritosSource,
        layout: { visibility: "none" },
        filter: ["==", ["get", "key"], ""],
        paint: { "line-color": "#ffffff", "line-width": 2.4 },
      });

      // The selected outline is two layers, a light casing under a dark line.
      // One stroke cannot be read against both a pale ramp step and a dark one,
      // and the boundary of the area you just clicked is the whole point of
      // clicking it — most of all for a barrio, which can be a few blocks
      // wedged between two others.
      map.addLayer({
        id: LAYERS.distritosSelectedCasing,
        type: "line",
        source: LAYERS.distritosSource,
        layout: { visibility: "none", "line-join": "round" },
        filter: ["==", ["get", "key"], ""],
        paint: { "line-color": "#ffffff", "line-width": 6, "line-opacity": 0.9 },
      });

      map.addLayer({
        id: LAYERS.distritosSelected,
        type: "line",
        source: LAYERS.distritosSource,
        layout: { visibility: "none", "line-join": "round" },
        filter: ["==", ["get", "key"], ""],
        paint: { "line-color": "#212121", "line-width": 2.6 },
      });

      // No symbol layer for the district names: MapLibre's `text-field` needs a
      // `glyphs` endpoint and this style is raster-only by design. The names are
      // DOM markers instead — see useDistritoLabels.

      setReady(true);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  return { containerRef, ready, mapRef };
}

/** Replace the buildings source, and frame whatever is now in it. */
export function showBuildings(
  map: maplibregl.Map,
  data: FeatureCollection,
  { fit = true } = {},
): void {
  // getSource is typed as the Source union; only GeoJSONSource has setData, and
  // the discriminant does not narrow it. Checking for the method itself rather
  // than casting keeps the guard honest — if the layer is ever rebuilt on a
  // vector source this returns instead of throwing at runtime.
  const source = map.getSource(LAYERS.buildingsSource) as
    | maplibregl.GeoJSONSource
    | undefined;
  if (typeof source?.setData !== "function") return;
  source.setData(data);

  if (!fit) return;
  const bounds = getBounds(data.features);
  // Null when a filter matched nothing. Leaving the viewport alone is right
  // there — flying somewhere arbitrary would suggest the filter found something.
  if (bounds) map.fitBounds(bounds, { padding: 40, duration: 600 });
}

/**
 * A GeoJSON source, or undefined.
 *
 * getSource is typed as the Source union and the discriminant does not narrow
 * it. Checking for setData itself rather than casting keeps the guard honest: if
 * a layer is ever rebuilt on a vector source this returns instead of throwing.
 */
function geojsonSource(
  map: maplibregl.Map,
  id: string,
): maplibregl.GeoJSONSource | undefined {
  const source = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
  return typeof source?.setData === "function" ? source : undefined;
}

export function setLayerVisible(
  map: maplibregl.Map,
  id: string,
  visible: boolean,
): void {
  if (map.getLayer(id)) {
    map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
  }
}

/** Which of the two readings the buildings layer is painting. */
export function setBuildingPaint(
  map: maplibregl.Map,
  mode: BuildingPaint,
): void {
  if (map.getLayer(LAYERS.buildingsFill)) {
    map.setPaintProperty(LAYERS.buildingsFill, "fill-color", BUILDING_PAINT[mode]);
  }
}

/** Replace the road segments. Never fits: the buildings already framed the view. */
export function showTramos(map: maplibregl.Map, data: FeatureCollection): void {
  geojsonSource(map, LAYERS.tramosSource)?.setData(data);
}

/** Replace the districts, and reveal the outlines that frame the city view. */
export function showDistritos(map: maplibregl.Map, data: FeatureCollection): void {
  geojsonSource(map, LAYERS.distritosSource)?.setData(data);
  // Boundaries and names stay up for the whole city view whether the choropleth
  // is on or not: their job is to say which district the certificates under the
  // cursor belong to, and that matters most precisely when the fill is off and
  // the letter colours are showing untinted.
  setLayerVisible(map, LAYERS.distritosLine, true);
  setLayerVisible(map, LAYERS.distritosHover, true);
  setLayerVisible(map, LAYERS.distritosSelectedCasing, true);
  setLayerVisible(map, LAYERS.distritosSelected, true);
}

/**
 * The choropleth fill, on or off.
 *
 * Only the fill follows the toggle. Without one beneath them the outlines carry
 * the whole boundary reading, so they darken and thicken; over a fill they only
 * need to trace an edge.
 */
export function setDistritoFill(map: maplibregl.Map, on: boolean): void {
  setLayerVisible(map, LAYERS.distritosFill, on);
  if (!map.getLayer(LAYERS.distritosLine)) return;
  map.setPaintProperty(LAYERS.distritosLine, "line-width", on ? 0.8 : 1.6);
  map.setPaintProperty(LAYERS.distritosLine, "line-color",
    on ? "rgba(33,33,33,0.35)" : "rgba(33,33,33,0.55)");
}

/** Ring one area by key on one layer, or none when the key is empty. */
export function highlightDistrito(
  map: maplibregl.Map,
  layer: string,
  key: string,
): void {
  if (map.getLayer(layer)) map.setFilter(layer, ["==", ["get", "key"], key]);
}

/**
 * The selected area's outline: casing and line together.
 *
 * One call, because the two layers are one mark. Setting them separately is how
 * a halo ends up drawn around an area that is no longer selected.
 */
export function selectArea(map: maplibregl.Map, key: string): void {
  highlightDistrito(map, LAYERS.distritosSelectedCasing, key);
  highlightDistrito(map, LAYERS.distritosSelected, key);
}

/**
 * Paint the choropleth for one metric.
 *
 * Nulls cannot go through `step` — it needs a number — so they are routed to the
 * neutral no-data colour by a `case` first. With too few distinct values to
 * classify, `breaks` is empty and the whole layer takes the ramp's last step
 * rather than pretending to five classes it cannot fill.
 */
export function paintDistritos(
  map: maplibregl.Map,
  metricKey: string,
  breaks: readonly number[],
  ramp: readonly string[],
): void {
  if (!map.getLayer(LAYERS.distritosFill)) return;

  if (!breaks.length) {
    map.setPaintProperty(LAYERS.distritosFill, "fill-color",
      ramp[ramp.length - 1] as string);
    return;
  }

  const flat = breaks.flatMap((b, i) => [b, ramp[i + 1] as string]);
  // Comparing against a null literal is valid in the style spec but absent from
  // @maplibre/maplibre-gl-style-spec's TypeScript model of ExpressionInputType,
  // so the cast is the one place that gap is acknowledged. It has to be this
  // test: `to-number` converts null to 0, and 0 is a real value for most of
  // these metrics — a district with no data would paint as the lowest class
  // rather than as no data.
  const expr = ["case",
    ["==", ["get", metricKey], null], SEQ_NONE,
    ["step", ["to-number", ["get", metricKey]], ramp[0] as string, ...flat],
  ] as unknown as maplibregl.ExpressionSpecification;

  map.setPaintProperty(LAYERS.distritosFill, "fill-color", expr);
}
